import type {
  GridDataItem,
  GridDataItemRaw,
  GridInputErrorMessage,
} from "@/types/grid";
import type {
  BulkImportChanges,
  BulkImportPageSetup,
} from "@/types/bulk-import";
import type {
  BaseLineDto,
  BaseWorkGroupDto,
  BaseEquipAreaViewDto,
  ImportErrorDto,
  LineDto,
  WorkGroupDto,
  EquipDto,
} from "@ckweb/ckfetch/connectables/equipmentAreaManagement";
import type {
  BaseDditDto,
  DditDto,
  GpitDto,
  PmitImportDto,
} from "@ckweb/ckfetch/connectables/taskListManagement";

/**
 * This composable is a wrapper around useImportFacilityStore, by adding the fetch functions for the different entities.
 * The main reason is that the store useImportFacilityStore, needs to be use inside a middleware, and fetch functions are not allowed there.
 */
export function useImportFacilityApi() {
  // Can't use useI18n here because this composable will be used in middlewares
  const { t, formatOptions } = useFormatOptions();
  const appStore = useAppStore();
  const importFacilityStore = useImportFacilityStore();
  const facilityStore = useFacilityStore();
  const { handleSafeExecution } = useSafeExecution();

  const updateFacility = useUpdateFacility();
  const fetchProductionLinesTemplate = useGetProductionLinesTemplate();
  const saveProductionLines = useSubmitProductionLines();
  const getEquipmentAreasTemplate = useGetEquipmentAreasTemplate();
  const saveEquipmentAreas = useSubmitEquipmentAreas();
  const getWorkGroupsTemplate = useGetWorkGroupsTemplate();
  const saveWorkGroups = useSubmitWorkGroups();
  const getManufacturersTemplate = useGetManufacturersTemplate();
  const saveManufacturers = useSubmitManufacturers();
  const getDailyDutiesTemplate = useGetDailyDutiesTemplate();
  const saveDailyDuties = useSubmitDailyDuties();
  const getGlobalTasksTemplate = useGetGlobalTasksTemplate();
  const saveGlobalTasks = useSubmitGlobalTasks();
  const getTaskListTemplate = useGetTaskListTemplate();
  const saveTaskList = useSubmitTaskList();
  const getSkillList = useGetSkillList();
  const getTaskHeightList = useGetEquipmentTaskHeightList();
  const getInspectionZonesList = useGetInspectionZonesList();
  const getProductionLinesWithId = useGetProductionLines();
  const getWorkGroupsWithId = useGetWorkGroups();
  const getEquipmentAreasWithId = useGetEquipmentAreas();

  const activeImportFacilityId = computed(
    () => importFacilityStore.activeImportFacilityId,
  );

  const isLoadingImportData = computed(() => importFacilityStore.isLoading);

  const draftSnapshot = computed({
    get: () => {
      if (!activeImportFacilityId.value) return;
      const value = importFacilityStore.draftSnapshotPerFacility.get(
        activeImportFacilityId.value,
      );
      // in case local storage is corrupted
      if (!(value && "data" in value && value.data instanceof Map)) return;
      return value;
    },
    set: (value) => {
      if (!activeImportFacilityId.value) return;
      if (!value) {
        importFacilityStore.draftSnapshotPerFacility.delete(
          activeImportFacilityId.value,
        );
        return;
      }

      importFacilityStore.draftSnapshotPerFacility.set(
        activeImportFacilityId.value,
        value,
      );
    },
  });

  const completedPercentage = computed(() => {
    const requiredList = importPagesList.value.filter(
      ({ optional }) => !optional,
    );
    const bulkItemsLength = requiredList.length;
    const completedItems = requiredList.filter(
      (item) => item.taskCompleted,
    ).length;
    return Math.ceil((completedItems / bulkItemsLength) * 100);
  });

  const updateFacilityStatus = async () => {
    if (!facilityStore.currentFacility?.id) return;

    const setupStatus = calculateFacilityStatus(
      facilityStore.currentFacility,
      completedPercentage.value === 100,
    );

    if (setupStatus === facilityStore.currentFacility.setupStatus) return;

    const payload = {
      ...facilityStore.currentFacility,
      setupStatus,
    };

    await updateFacility.safeMutate(facilityStore.currentFacility.id, payload, {
      enableGlobalNotifications: false,
      showGlobalLoadingIndicator: false,
    });
    const updatedData = toValue(updateFacility.data);
    if (toValue(updateFacility.errors) || !updatedData) return;

    facilityStore.currentFacility = updatedData;

    if (setupStatus === "setupComplete") {
      appStore.addSuccessMessage("cksetup.imports.completedSuccessMessage");
    }
  };

  const mutateErrorOnItem = <D extends GridDataItemRaw>(
    dataItem: GridDataItem<D>,
    {
      errors,
      warnings,
      propertyName,
    }: {
      errors?: string[];
      warnings?: string[];
      propertyName?: string;
    },
  ) => {
    const errorMessages = [
      ...(errors ?? []).map((message) => ({
        message,
        dismissible: true,
        type: "error",
      })),
      ...(warnings ?? []).map((message) => ({
        message,
        dismissible: true,
        type: "warning",
      })),
    ] as GridInputErrorMessage[];
    if (!errorMessages.length) return;

    const field = propertyName as StringKeyOf<D> | undefined;

    dataItem.$persistentDataErrors = appendItemDataErrors(
      dataItem.$persistentDataErrors,
      errorMessages,
      field,
    );
    dataItem.$flatErrors = flattenDataItemErrors(dataItem);
    if (field) makeTouchedRecord(dataItem, [field]);
  };

  const patchDataErrors = <D extends GridDataItemRaw>(
    deltaChanges: ImportErrorDto[],
    changes: BulkImportChanges<D>,
    idField: StringKeyOf<D> | "$uniqueId",
    original: GridDataItem<D>[],
  ): GridDataItem<D>[] => {
    const newPatchedData = [...original];
    if (deltaChanges && deltaChanges.length > 0 && changes) {
      deltaChanges.forEach(({ property, errors, warnings }) => {
        const result = parseResponseErrorRecordPath(property, [
          ...errors,
          ...warnings,
        ]);
        if (!result) {
          devConsole.error("Field not found for", {
            property,
            errors,
            warnings,
          });
          return;
        }
        const { indexOrId, listName, propertyName } = result;

        if (listName === "addedItems") {
          const addedItem = changes.addedItems[indexOrId];
          if (!addedItem) {
            devConsole.error(`Added item with index ${indexOrId} not found`, {
              errors,
            });
            return;
          }
          const addedGridDataItem = makeGridDataItem(addedItem);
          mutateErrorOnItem(addedGridDataItem, {
            errors,
            warnings,
            propertyName,
          });
          newPatchedData.push(addedGridDataItem);
          return;
        }

        if (listName === "tasks" || listName === "deletedTasks") {
          const task = newPatchedData[indexOrId];
          if (!task) {
            devConsole.error(`Task item with index ${indexOrId} not found`, {
              errors,
            });
            return;
          }
          mutateErrorOnItem(task, { errors, warnings, propertyName });
          return;
        }
        const dataItem = newPatchedData.find(
          (item) => item[idField] === indexOrId,
        );

        if (!dataItem) {
          devConsole.error(`Item with id ${indexOrId} not found`, {
            errors,
          });
          return;
        }

        if (listName === "updatedItems") {
          const patch = changes.updatedItems.find(
            (change) => change[idField] === indexOrId,
          );
          // TODO: this is not assigning correctly, also the errors disappear
          Object.assign(dataItem, patch);
        }
        mutateErrorOnItem(dataItem, { errors, warnings, propertyName });
      });
    }

    return newPatchedData;
  };

  const productionLinesSetup = computed<
    BulkImportPageSetup<ProductionLineSchema>
  >(() => {
    const setup = getProductionLinesPageConfig();

    return {
      ...setup,
      count: importFacilityStore.productionLines.length,
      taskCompleted: importFacilityStore.productionLines.length > 0,
      saveErrorResponse: toValue(saveProductionLines.errors),
      dataItems: () => importFacilityStore.productionLines,
      loadGridConfigFn: getProductionLinesGridConfig,
      downloadTemplateFn: fetchProductionLinesTemplate.safeDownload,
      loadGridDataFn: importFacilityStore.getProductionLines,
      saveGridDataFn: async (_, changes) => {
        // TODO: this will be move to an abstract util when we have more patches (sorry for the bad implementation)
        if (!changes) return [];

        const deltaChanges = await saveProductionLines.safeMutate({
          updatedItems: changes.updatedItems as LineDto[],
          addedItems: changes.addedItems.map(
            ({ number, ...rest }) => rest,
          ) as BaseLineDto[],
          deletedItems: changes.deletedItems.map(({ number }) => number!),
        });
        if (!deltaChanges) return; // on error scenario
        if (!deltaChanges.length) return []; // on success scenario

        return (original) =>
          patchDataErrors(deltaChanges, changes, "number", original);
      },
    };
  });

  const workGroupsSetup = computed<BulkImportPageSetup<WorkGroupSchema>>(() => {
    const setup = getWorkGroupsPageConfig();

    return {
      ...setup,
      count: importFacilityStore.workGroups.length,
      taskCompleted: importFacilityStore.workGroups.length > 0,
      saveErrorResponse: () => toValue(saveWorkGroups.errors),
      dataItems: () => importFacilityStore.workGroups,
      loadGridConfigFn: getWorkGroupsGridConfig,
      downloadTemplateFn: getWorkGroupsTemplate.safeDownload,
      loadGridDataFn: importFacilityStore.getWorkGroups,
      saveGridDataFn: async (data, changes) => {
        const noChangeoverRows = data.filter(
          (item) => item.changeoverLevel === changeOverLevels.noChangeover,
        );
        if (!noChangeoverRows.length) {
          appStore.addError(
            t("cksetup.imports.work-groups.noChangeoverRowRequiredError"),
          );
          return;
        }

        if (!changes) return [];

        const deltaChanges = await saveWorkGroups.safeMutate({
          updatedItems: changes.updatedItems as WorkGroupDto[],
          addedItems: changes.addedItems.map(
            ({ number, ...rest }) => rest,
          ) as BaseWorkGroupDto[],
          deletedItems: changes.deletedItems.map(({ number }) => number!),
        });
        if (!deltaChanges) return; // on error scenario
        if (!deltaChanges.length) return []; // on success scenario

        return (original) =>
          patchDataErrors(deltaChanges, changes, "number", original);
      },
    };
  });

  const equipmentAreasSetup = computed<
    BulkImportPageSetup<EquipmentAreaSchema>
  >(() => {
    const setup = getEquipmentAreasPageConfig();

    return {
      ...setup,
      count: importFacilityStore.equipmentAreas.length,
      taskCompleted: importFacilityStore.equipmentAreas.length > 0,
      taskLocked:
        importFacilityStore.productionLines.length === 0 ||
        importFacilityStore.workGroups.length === 0,
      saveErrorResponse: () => toValue(saveEquipmentAreas.errors),
      dataItems: () => importFacilityStore.equipmentAreas,
      loadGridConfigFn: async () => {
        if (!facilityStore.currentFacility) {
          throw new Error("Facility not found");
        }
        const productionLines = await getProductionLinesWithId.safeFetch();
        const workGroups = await getWorkGroupsWithId.safeFetch();
        const taskHeights = await getTaskHeightList.safeFetch();
        const inspectionZones = await getInspectionZonesList.safeFetch();

        return getEquipmentAreasGridConfig({
          facilityDto: facilityStore.currentFacility,
          productionLines,
          workGroups,
          taskHeights,
          inspectionZones,
        });
      },
      downloadTemplateFn: getEquipmentAreasTemplate.safeDownload,
      loadGridDataFn: importFacilityStore.getEquipmentAreas,
      saveGridDataFn: async (_, changes) => {
        // TODO: this will be move to an abstract util when we have more patches (sorry for the bad implementation)
        if (!changes) return [];

        const deltaChanges = await saveEquipmentAreas.safeMutate({
          updatedItems: changes.updatedItems as EquipDto[],
          addedItems: changes.addedItems.map(
            ({ number, ...rest }) => rest,
          ) as BaseEquipAreaViewDto[],
          deletedItems: changes.deletedItems.map(({ number }) => number!),
        });
        if (!deltaChanges) return; // on error scenario
        if (!deltaChanges.length) return []; // on success scenario

        return (original) =>
          patchDataErrors(deltaChanges, changes, "number", original);
      },
    };
  });

  // TODO: to be shown in latter iterations
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const manufacturersSetup = computed<BulkImportPageSetup<ManufacturerSchema>>(
    () => {
      const setup = getManufacturersPageConfig();

      return {
        ...setup,
        count: importFacilityStore.manufacturers.length,
        taskCompleted: importFacilityStore.manufacturers.length > 0,
        saveErrorResponse: () => toValue(saveManufacturers.errors),
        dataItems: () => importFacilityStore.manufacturers,
        loadGridConfigFn: getManufacturersGridConfig,
        downloadTemplateFn: getManufacturersTemplate.safeDownload,
        loadGridDataFn: importFacilityStore.getManufacturers,
        saveGridDataFn: (data: ManufacturerSchema[]) =>
          // TODO: we should not include `Various` item when saving the manufacturers
          saveManufacturers.safeMutate(
            $ck._.reject(data, isVariousManufacturer),
          ),
      };
    },
  );

  const dailyDutiesSetup = computed<BulkImportPageSetup<DailyDutySchema>>(
    () => {
      const setup = getDailyDutiesPageConfig();

      return {
        ...setup,
        count: importFacilityStore.dailyDuties.length,
        taskCompleted: importFacilityStore.dailyDuties.length > 0,
        taskLocked: importFacilityStore.equipmentAreas.length === 0,
        saveErrorResponse: () => toValue(saveDailyDuties.errors),
        dataItems: () => importFacilityStore.dailyDuties,
        loadGridConfigFn: async () => {
          const equipmentAreas = await getEquipmentAreasWithId.safeFetch();
          const skillTypes = await getSkillList.safeFetch();
          return getDailyDutiesGridConfig({
            equipmentAreas,
            skillTypes,
          });
        },
        downloadTemplateFn: getDailyDutiesTemplate.safeDownload,
        loadGridDataFn: importFacilityStore.getDailyDuties,
        saveGridDataFn: async (_, changes) => {
          // TODO: this will be move to an abstract util when we have more patches (sorry for the bad implementation)
          if (!changes) return [];

          const deltaChanges = await saveDailyDuties.safeMutate({
            updatedItems: changes.updatedItems as DditDto[],
            addedItems: changes.addedItems.map(
              ({ number, ...rest }) => rest,
            ) as BaseDditDto[],
            deletedItems: changes.deletedItems.map(({ number }) => number!),
          });
          if (!deltaChanges) return; // on error scenario
          if (!deltaChanges.length) return []; // on success scenario

          return (original) =>
            patchDataErrors(deltaChanges, changes, "number", original);
        },
      };
    },
  );

  const globalBeforeTasksSetup = computed<
    BulkImportPageSetup<GlobalTaskSchema>
  >(() => {
    const setup = getGlobalTasksPageConfig("before");

    return {
      ...setup,
      count: importFacilityStore.globalBeforeTasks.length,
      taskCompleted: importFacilityStore.globalBeforeTasks.length > 0,
      saveErrorResponse: () => toValue(saveGlobalTasks.errors),
      dataItems: () => importFacilityStore.globalBeforeTasks,
      loadGridConfigFn: getGlobalTasksGridConfig,
      downloadTemplateFn: () => getGlobalTasksTemplate.safeDownload("before"),
      loadGridDataFn: importFacilityStore.getGlobalBeforeTasks,
      saveGridDataFn: async (_, changes) => {
        // TODO: this will be move to an abstract util when we have more patches (sorry for the bad implementation)
        if (!changes) return [];

        const deltaChanges = await saveGlobalTasks.safeMutate("before", {
          updatedItems: changes.updatedItems as GpitDto[],
          addedItems: changes.addedItems.map(
            ({ sequence, ...rest }) => rest,
          ) as GpitDto[],
          deletedItems: changes.deletedItems.map(({ sequence }) => sequence!),
        });
        if (!deltaChanges) return; // on error scenario
        if (!deltaChanges.length) return []; // on success scenario

        return (original) =>
          patchDataErrors(deltaChanges, changes, "sequence", original);
      },
    };
  });

  const globalAfterTasksSetup = computed<BulkImportPageSetup<GlobalTaskSchema>>(
    () => {
      const setup = getGlobalTasksPageConfig("after");

      return {
        ...setup,
        count: importFacilityStore.globalAfterTasks.length,
        taskCompleted: importFacilityStore.globalAfterTasks.length > 0,
        saveErrorResponse: () => toValue(saveGlobalTasks.errors),
        dataItems: () => importFacilityStore.globalAfterTasks,
        loadGridConfigFn: getGlobalTasksGridConfig,
        downloadTemplateFn: () => getGlobalTasksTemplate.safeDownload("after"),
        loadGridDataFn: importFacilityStore.getGlobalAfterTasks,
        saveGridDataFn: async (_, changes) => {
          // TODO: this will be move to an abstract util when we have more patches (sorry for the bad implementation)
          if (!changes) return [];

          const deltaChanges = await saveGlobalTasks.safeMutate("after", {
            updatedItems: changes.updatedItems as GpitDto[],
            addedItems: changes.addedItems.map(
              ({ sequence, ...rest }) => rest,
            ) as GpitDto[],
            deletedItems: changes.deletedItems.map(({ sequence }) => sequence!),
          });
          if (!deltaChanges) return; // on error scenario
          if (!deltaChanges.length) return []; // on success scenario

          return (original) =>
            patchDataErrors(deltaChanges, changes, "sequence", original);
        },
      };
    },
  );

  const taskListSetup = computed<BulkImportPageSetup<TaskListSchema>>(() => {
    const setup = getTaskListPageConfig();

    return {
      ...setup,
      count: importFacilityStore.taskList.length,
      taskCompleted: importFacilityStore.taskList.length > 0,
      taskLocked: importFacilityStore.equipmentAreas.length === 0,
      saveErrorResponse: () => toValue(saveTaskList.errors),
      dataItems: () => importFacilityStore.taskList,
      loadGridConfigFn: async () => {
        const equipmentAreas = await getEquipmentAreasWithId.safeFetch();
        const skillTypes = await getSkillList.safeFetch();
        return getTaskListGridConfig({
          equipmentAreas,
          skillTypes,
        });
      },
      downloadTemplateFn: getTaskListTemplate.safeDownload,
      loadGridDataFn: importFacilityStore.getTaskList,
      transformDataItemFn: async (mappedRow, computedConfig) => {
        const item = await transformDataItem(
          mappedRow,
          computedConfig,
          formatOptions.value,
        );
        item.item2 = item.item2 ?? "";

        if (typeof item.isSubtask !== "boolean") {
          item.isSubtask = compareCellValues(item.isSubtask, "subtask")
            ? true
            : compareCellValues(item.isSubtask, "task")
              ? false
              : item.isSubtask;
        }

        return item;
      },

      appendDataFn: (oldData, newData, fields) => {
        // force replace imported data
        return newData.map((rawItem) => {
          const item = makeGridDataItem(rawItem);
          item.$dirty = makeDirtyRecord(item, fields);
          return item;
        });
      },
      saveGridDataFn: async (dataItems, changes) => {
        // TODO: this will be move to an abstract util when we have more patches (sorry for the bad implementation)
        if (!changes) return [];

        const deltaChanges = await saveTaskList.safeMutate({
          reasonForChange: changes.reason!,
          tasks: dataItems as PmitImportDto[],
          deletedTasks: changes.deletedItems as PmitImportDto[],
        });
        if (!deltaChanges) return; // on error scenario
        if (!deltaChanges.length) return []; // on success scenario

        return (original) =>
          patchDataErrors(deltaChanges, changes, "$uniqueId", original);
      },
    };
  });

  const importPagesList = computed(
    () =>
      [
        productionLinesSetup.value,
        workGroupsSetup.value,
        equipmentAreasSetup.value,
        dailyDutiesSetup.value,
        globalBeforeTasksSetup.value,
        globalAfterTasksSetup.value,
        taskListSetup.value,
      ] as unknown as BulkImportPageSetup[],
  );

  const loadAllImportedData = async () => {
    importFacilityStore.activeImportFacilityId =
      facilityStore.currentFacility?.id;

    await handleSafeExecution(
      () =>
        Promise.all(
          importPagesList.value.map(({ loadGridDataFn }) => loadGridDataFn()),
        ),
      {
        executionName: `Load all imported data for facility ${importFacilityStore.activeImportFacilityId}`,
        showGlobalLoadingIndicator: true,
      },
    );
  };

  function hasPageDraftSnapshot(pageId?: string) {
    if (!pageId) return false;
    return draftSnapshot.value?.data.has(pageId);
  }

  function getPageDraftSnapshot(pageId?: string) {
    if (!pageId) return;
    return draftSnapshot.value?.data.get(pageId);
  }

  function setPageDraftSnapshot(pageId?: string, changes?: BulkImportChanges) {
    if (!pageId) return false;
    if (!changes || !hasBulkImportChanges(changes))
      return clearPageDraftSnapshot(pageId);

    if (!draftSnapshot.value?.data?.size) {
      draftSnapshot.value = {
        updated: new Date(),
        data: new Map(),
      };
    } else {
      draftSnapshot.value.updated = new Date();
    }

    return draftSnapshot.value.data.set(pageId, changes);
  }

  function clearPageDraftSnapshot(pageId?: string) {
    if (!pageId) return false;
    return draftSnapshot.value?.data.delete(pageId);
  }

  return {
    activeImportFacilityId,
    isLoadingImportData,
    importPagesList,
    completedPercentage,
    loadAllImportedData,
    hasPageDraftSnapshot,
    getPageDraftSnapshot,
    setPageDraftSnapshot,
    clearPageDraftSnapshot,
    updateFacilityStatus,
  };
}
